import { Coupon } from 'src/app/model/coupon';
import { Product } from 'src/app/model/product';
import { Feed } from './feed';
import { Ticket } from './product';
import { Review } from './review';
import { NonMember, NonMemberCreateDto, User } from './user';

export class PaymentCreateDto {
  constructor(
    public order: OrderCreateDto | NonMemberOrderCreateDto,
    public pgName: PG,
    public pgOrderId: string,
    public payAt: Date,
    public totalPrice: number,
    public payMethod: PayMethod,
    public payPrice: number,
    public payCommissionPrice: number,
    public result: boolean,
    public resultMessage: string,
    public cardName?: string,
    public cardNum?: string,
    public cardReceipt?: string,
    public bankName?: string,
    public bankNum?: string,
    public externalOrderId?: string,
    public createdAt?: Date,
    public updatedAt?: Date,
    public skipCapacityCheck?: boolean,
  ) {}
}

export class OrderCreateDto {
  constructor(
    public userId: number,
    public productId: number,
    public couponId: number,
    public point: number,
    public orderAt: Date,
    public orderItems: OrderItemCreateDto[],
  ) {}
}

export class NonMemberOrderCreateDto {
  constructor(
    public productId: number,
    public orderAt: Date,
    public orderItems: OrderItemCreateDto[],
    public nonMember: NonMemberCreateDto,
    public couponId?: number,
  ) {}
}

export class OrderItemCreateDto {
  constructor(
    public ticketId: number,
    public count: number,
  ) {}
}

export class Participant {
  constructor(
    public id: number,
    public name: string,
    public phoneNumber: string,
    public boardingLocation: string,
    public isPurchaser: boolean,
    public identificationNumber: string,
    public gender: string,
    public remarks: string,
    public isInfant: boolean,
    public customFields: Record<string, unknown>,
    public createdAt: Date,
    public updatedAt: Date,
    public ticketId?: number | null,
    public orderItemId?: number | null,
  ) {}
}

export class Payment {
  currency?: string;
  reservationChangeFee?: number;
  constructor(
    public id: number,
    public order: Order,
    public pgName: PG,
    public pgOrderId: string,
    public payAt: Date,
    public totalPrice: number,
    public payMethod: PayMethod,
    public payPrice: number,
    public payCommissionPrice: number,
    public result: boolean,
    public resultMessage?: string,
    public cardName?: string,
    public cardNum?: string,
    public cardReceipt?: string,
    public bankName?: string,
    public bankNum?: string,
    public createdAt?: Date,
    public updatedAt?: Date,
    public participants?: Participant[],
    /**
     * 참가자 조회가 실패했음을 표시한다. `participants: []` 는 '참가자가 없는 예약'과 구분되지 않아
     * 명단 CSV 가 공백 행을 정상 행처럼 싣는다 — 내려받기를 막으려면 그 사실이 결제에 남아야 한다.
     */
    public participantsLoadFailed?: boolean,
  ) {}
  isRequestReview = false;
  reviews?: Review[];
  /**
   * @deprecated 부분환불 N회 지원을 위해 `paymentCancels`(1:N) 로 전환 중.
   * 전환 기간 동안 "가장 최근 환불 레코드"를 가리킨다. 누적 환불액은 `paymentCancels` 합계를 사용.
   *
   * ⚠️ 이 필드의 truthiness 로 "예약 취소 여부"를 판정하지 말 것 — 예약변경 부분환불도
   * 레코드를 남기지만 예약은 살아있다. 취소 여부는 `isCancelled` 로 판정하고,
   * 이 필드는 환불 내역 표시 용도로만 쓴다(궁극적으로 `paymentCancels` 로 대체).
   */
  paymentCancel?: PaymentCancel;
  /** 부분환불 N회 + 최종환불을 건별로 보관 (payment 1:N payment_cancel). */
  paymentCancels?: PaymentCancel[];
  /**
   * 예약 취소(완전) 여부. 환불 레코드(`paymentCancels`) 존재와 독립된 명시적 신호다.
   * 부분환불(예약변경 차액)이 쌓여도 예약이 살아있으면 false, 예약이 완전 취소될 때만 true.
   */
  isCancelled?: boolean;
  settlement?: { status: string };
  feeds?: Feed[];
  error?: any;
  tickets?: Ticket[];
  ticketList: TicketFixedPrice[];
  lastPurchasedAt?: Date;
  buyerNotificationSentAt?: Date;
  hostNotificationSentAt?: Date;
}

export class PreOrder {
  id: number;
  pgName: PG;
  preOrderKey: string;
  pgOrderId: string;
  totalPrice: number;
  payMethod: PayMethod;
  price: number;
  payCommissionPrice: number;
  createdAt: Date;
  updatedAt: Date;
  feeds?: Feed[];
  error?: any;
  product: Product;
  user?: User;
  nonMember?: NonMember;
  coupon?: Coupon;
  preOrderItems: OrderItem[];
  tickets?: Ticket[];
  ticketList: TicketFixedPrice[];
  paymentId?: number;
  status?: PreOrderStatus;
}

export interface TicketFixedPrice {
  id?: number;
  date: string;
  name: string;
  parent?: Ticket;
  price: number;
  quantity: number;
  isHidden?: boolean;
}

export interface PaymentCancel {
  id: number;
  reason: string;
  refundPrice: number;
  cancelAt: Date;
  refundCoupon: boolean;
  refundPoint: boolean;
  payment: Payment;
  buyerNotificationSentAt?: Date;
  hostNotificationSentAt?: Date;
}

export interface PaymentCancelDto {
  preserveReservation?: boolean;
  reason: string;
  // 취소 사유 코드(분석키). 의미는 docs/insights/analysis-guide.md 참고
  reasonCode?: string;
  refundPrice: number;
  refundCoupon?: boolean;
  refundPoint?: boolean;
  requester?: string;
  payment: number;
  // 서버가 "DB 만 취소" 로 받아들이는 유일한 근거 — 없거나 false 면 잔액 남은 결제의 DB 취소는 PG 와 무관하게 거절된다.
  skipCancelPGPayment: boolean;
  bankAccount?: {
    bankName: string;
    accountNumber: string;
    accountHolderName: string;
  };
}

export interface Order {
  id: number;
  user: User;
  nonMember: NonMember;
  product: Product;
  coupon: Coupon;
  /** 결제 시점 쿠폰 할인액(NNB-823). `null` 은 컬럼 도입 전 주문이고, 이 경로는 재계산하지 않는다. */
  couponDiscount?: number | null;
  point: number;
  orderAt: Date;
  description: string;
  zipCode?: string;
  address?: string;
  detailAddress?: string;
  orderItems: OrderItem[];
  payment?: Payment;
}

export interface OrderItem {
  id: number;
  order: Order;
  ticket: Ticket;
  count: number;
  // 결제 시점 티켓 가격 스냅샷. null=과거분/미기록 → getTicketPrice 폴백.
  price?: number | null;
}

export enum PG {
  NAVER_PAY = 'NAVER_PAY',
  KAKAO_PAY = 'KAKAO_PAY',
  PAYPLE = 'PAYPLE',
  PORTONE = 'PORTONE',
  TOSSPAYMENTS = 'TOSSPAYMENTS',
  PAYPAL = 'PAYPAL',
}

export enum PayMethod {
  CARD = 'CARD',
  TRANSFER = 'TRANSFER',
  DIRECT_CARD = 'DIRECT_CARD',
  SIMPLE_CARD = 'SIMPLE_CARD',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  KAKAO_PAY = 'KAKAO_PAY',
  NAVER_PAY = 'NAVER_PAY',
  TOSS_PAY = 'TOSS_PAY',
  APPLE_PAY = 'APPLE_PAY',
  SAMSUNG_PAY = 'SAMSUNG_PAY',
  PAYPAL = 'PAYPAL',

  // 수기 입력 결제 방법
  FREE = 'FREE',
  DIRECT_TRANSFER = 'DIRECT_TRANSFER',
  NAVER_SMARTSTORE = 'NAVER_SMARTSTORE',
  GMARKET = 'GMARKET',
  YEOGI = 'YEOGI',
  KKDAY = 'KKDAY',
  KLOOK = 'KLOOK',
  FRIP = 'FRIP',
  // 플랫폼 PG 결제(해외 카드) — 외부 판매채널이 아니다.
  OVERSEAS_CARD = 'OVERSEAS_CARD',

  // PG 결제 (수기 등록 시 PG 결제 내역이 있을 때)
  PAYMENT_GATEWAY = 'PAYMENT_GATEWAY',
}

export interface PaymentSearchDto {
  page?: number;
  limit?: number;
  productId?: number;
  isBusiness?: boolean;
  start?: Date;
  end?: Date;
  excludeCancel?: boolean;
  searchTerm?: string;
  productIds?: number[];
  paymentId?: number;
  paymentIds?: number[];
  includeParticipants?: boolean;
  orderSearchTerm?: string;
}

/**
 * `GET /api/payments/calendar` 응답 한 칸.
 * @see apps/nnb-nestjs/src/payment/payment-calendar.projection.ts 의 같은 이름 인터페이스
 */
export interface PaymentCalendarGroup {
  readonly date: string;
  readonly productId: number;
  readonly title: string;
  readonly color: string | null;
  readonly photo: string | null;
  readonly personCount: number;
  readonly paymentIds: readonly number[];
}

/** 캘린더 월 조회가 실제로 쓰는 조건. 이 엔드포인트는 페이지네이션을 하지 않는다. */
export type PaymentCalendarSearchDto = Pick<PaymentSearchDto, 'start' | 'end' | 'searchTerm' | 'excludeCancel'>;

export interface MyPaymentSearchDto {
  page: number;
  limit: number;
  isLast: boolean;
  userId?: number;
  excludeCancel?: boolean;
  includeProductHashtag?: boolean;
  includeParticipants?: boolean;
}

export interface PaymentSlectInfo {
  id: number;
  tickets: Ticket[];
}

export interface HostStatistics {
  data: number;
  diff: number;
}

export interface CanReservationItemDto {
  optionId: number;
  count: number;
}

export interface CanReservationDto {
  options: CanReservationItemDto[];
}

export interface NaverPayCash {
  npointCashAmount: number;
  giftCardCashAmount: number;
  primaryCashAmount: number;
  primaryPayMeans: string;
  totalCashAmount: number;
  supplyCashAmount: number;
  vatCashAmount: number;
}

export enum PreOrderStatus {
  PENDING = 'PENDING', // 입금 대기 중
  COMPLETED = 'COMPLETED', // 결제 완료
  EXPIRED = 'EXPIRED', // 기한 만료
  CAPACITY_EXCEEDED = 'CAPACITY_EXCEEDED', // 정원 초과로 취소됨
}

export class ProductRefundPolicy {
  daysBeforeStart: number;
  refundRate: string;
  description: string;
  fullRefundDate?: string;
}

export class ProductRefundPolicyResponse {
  policies: ProductRefundPolicy[];
}
